/**
 * Schema markup composable for SEO
 * Generates JSON-LD structured data for various content types
 */

export const useSchemaMarkup = () => {
  const generateEventSchema = (config: {
    name: string;
    description: string;
    startDate: string;
    endDate: string;
    location: string;
    image?: string;
    url?: string;
  }) => {
    return {
      "@context": "https://schema.org",
      "@type": "Event",
      name: config.name,
      description: config.description,
      startDate: config.startDate,
      endDate: config.endDate,
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
      location: {
        "@type": "Place",
        name: config.location,
        address: {
          "@type": "PostalAddress",
          addressCountry: "PK",
        },
      },
      image: config.image || "https://rozay.today/icon.png",
      url: config.url || "https://rozay.today",
    };
  };

  const generateLocalBusinessSchema = (config: {
    name: string;
    description: string;
    city: string;
    country?: string;
    url?: string;
  }) => {
    return {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: config.name,
      description: config.description,
      address: {
        "@type": "PostalAddress",
        addressLocality: config.city,
        addressCountry: config.country || "PK",
      },
      url: config.url || "https://rozay.today",
      sameAs: [
        "https://facebook.com/rozay",
        "https://twitter.com/rozay",
        "https://instagram.com/rozay",
      ],
    };
  };

  const generateFAQSchema = (
    faqs: Array<{ question: string; answer: string }>,
  ) => {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };
  };

  const generateWebPageSchema = (config: {
    name: string;
    description: string;
    url: string;
  }) => {
    return {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: config.name,
      description: config.description,
      url: config.url,
      isPartOf: {
        "@type": "WebSite",
        name: "Rozay.today",
        url: "https://rozay.today",
      },
    };
  };

  const generateOrganizationSchema = () => {
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Rozay.today",
      description: "Ramadan 2026 Pakistan - Accurate Sehri & Iftar Times",
      url: "https://rozay.today",
      logo: "https://rozay.today/logo.png",
      sameAs: [
        "https://facebook.com/rozay",
        "https://twitter.com/rozay",
        "https://instagram.com/rozay",
      ],
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "Customer Support",
        url: "https://rozay.today",
      },
    };
  };

  const generateBreadcrumbSchema = (
    items: Array<{ name: string; url: string }>,
  ) => {
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    };
  };

  const addSchema = (schema: Record<string, any>) => {
    useHead({
      script: [
        {
          type: "application/ld+json",
          innerHTML: JSON.stringify(schema),
          async: true,
        },
      ],
    });
  };

  return {
    generateEventSchema,
    generateLocalBusinessSchema,
    generateFAQSchema,
    generateWebPageSchema,
    generateOrganizationSchema,
    generateBreadcrumbSchema,
    addSchema,
  };
};
